import React, { useEffect } from 'react'
import { Grid, Typography, IconButton, Tooltip, Chip } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import { Replay, Autorenew, EditOff } from '@mui/icons-material'
import ButtonDialog from '~/components/ButtonDialog'
import useOccurrenceState from '../../stores/OccurrenceState'
import { reopenOccurrence } from '~/features/Occurrence/services/index'
import notify from '~/utils/notify'
import { useHookstate, type State } from '@hookstate/core'

import OccurrenceRecallModal, {
  dataOccurrenceDefault,
} from '~/features/Occurrence/components/OccurrenceRecallModal'
import SaveButton from '~/components/SaveButton'
import IDispatchOccurrence from '../../interfaces/IDispatchOccurrence'
import { useLocation } from 'react-router-dom'
import useSurveillanceReportingState from '~/features/VideoMonitoring/stores/SurveillanceReportingState'
import OccurrenceCopyButton from '../OccurrenceCopyButton'
import RequestClosureButton from '../ButtonRequestClosure'

interface dataOccurrenceProps {
  readonly type_id: null | string | number
  readonly subtype_id: null | string | number
  readonly city_id: null | string | number
  readonly latitude: number | null
  readonly longitude: number | null
  readonly location: string | null
}
interface ButtonActionsOccurrenceContainerProps {
  readonly dispatches: State<IDispatchOccurrence[]>;
}

// eslint-disable-next-line max-lines-per-function
export default function ButtonActionsOccurrenceContainer({ dispatches }: ButtonActionsOccurrenceContainerProps) {
  const { translate } = useLang()
  const {
    occurrence,
    occurrenceStatusActions,
    updateOccurrence,
    syncOccurrenceEditAttendent,
  } = useOccurrenceState()
  const isDisabled = useHookstate(true)
  const listStatus = useHookstate<readonly IDispatchOccurrence[]>([])
  const dataOccurrence = useHookstate<dataOccurrenceProps>(
    dataOccurrenceDefault
  )
  const recallConfirm = useHookstate(false)
  const openRequester = useHookstate(false)
  const { pathname } = useLocation()
  const isVideoMonitoringPage = pathname === '/video-monitoring'
  const { surveillanceReportingStates } = useSurveillanceReportingState()
  const { searchDispatches, showCheckbox, searchOccurrence } =
    surveillanceReportingStates()

  const action = () => {
    reopenOccurrence({ id: occurrence().id.get() }).then((response) => {
      if (response.status === 200) {
        notify({
          message: translate('Occurrence reopen Sucessfully!'),
          type: 'success',
        })
        isDisabled.set(true)
        occurrence().closed_at.set(null)
      }
    })
  }

  const verifyDispatchClosed = () => {
    const existDispatchClosed = dispatches?.get().filter((statusDispatch) => statusDispatch.status.id === 8)
    listStatus.set(existDispatchClosed)

    if (listStatus?.length > 0) {
      isDisabled.set(false)
    } else {
      isDisabled.set(true)
    }
  }

  useEffect(() => {
    verifyDispatchClosed()
  }, [dispatches.get()])

  const openRecall = () => {
    recallConfirm.set(true)
  }

  useEffect(() => {
    dataOccurrence.merge({
      type_id: occurrence().type.id.get(),
      subtype_id: occurrence().subtype.id.get(),
      location: occurrence().location.get(),
      latitude: occurrence().latitude.get(),
      longitude: occurrence().longitude.get(),
      city_id: occurrence().city.id.get(),
    })
  }, [occurrence()])

  return (
    <Grid container padding={1} spacing={1} sx={{ display: 'flex' }}>
      <Grid item>
        <Tooltip title={translate('Reopen Occurrence')}>
          <span style={{ display: 'inline-block' }}>
            <ButtonDialog
              titleButton={translate('Reopen Occurrence')}
              iconButton={<Replay />}
              disabled={isDisabled.get()}
              action={action}
              dialogTitle={translate('Reopen Occurrence')}
              dialogContent={
                <Typography>
                  {translate('Do you want to reopen this occurrence?')}
                </Typography>
              }
            />
          </span>
        </Tooltip>
      </Grid>
      <Grid item>
        <Tooltip title={translate('Recall')}>
          <span style={{ display: 'inline-block' }}>
            <IconButton
              disabled={!isDisabled.get()}
              onClick={() => openRecall()}
            >
              <Autorenew />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid item>
        <OccurrenceCopyButton isDisabled={!isDisabled.get()} />
      </Grid>
      <Grid>
        <RequestClosureButton
          occurrenceId={occurrence().id}
          closureRequested={occurrence().closure_requested}
          closedAt={occurrence().closed_at}
          isDisabled={!isDisabled.get()}
        />
      </Grid>
      {isVideoMonitoringPage && (
        <Grid item sx={{ mt: 1 }}>
          <Tooltip title={translate('Add Report')}>
            <Chip
              onClick={() => {
                searchDispatches.set(dispatches.get()[0].code)
                searchOccurrence.set(String(occurrence().id.get()))
              }}
              label={
                <Typography variant="subtitle1">
                  {translate('Add')}
                </Typography>
              }
            />
          </Tooltip>
        </Grid>
      )}
      {isVideoMonitoringPage && (
        <Grid item sx={{ mt: 1 }}>
          <Tooltip title={translate('Associate to Report')}>
            <Chip
              onClick={() => {
                searchDispatches.set(dispatches.get()[0].code)
                searchOccurrence.set(String(occurrence().id.get()))
                showCheckbox.set(true)
              }}
              label={
                <Typography variant="subtitle1">
                  {translate('Associate')}
                </Typography>
              }
            />
          </Tooltip>
        </Grid>
      )}
      {recallConfirm.get() ? (
        <OccurrenceRecallModal
          open={recallConfirm}
          openRequester={openRequester}
          occurrenceId={Number(occurrence().id.get())}
          redirectOccurrence={true}
          dataOccurrence={dataOccurrence}
        />
      ) : (
        false
      )}
      {occurrenceStatusActions().editingOccurrence.get() ? (
        <SaveButton
          canSave={occurrenceStatusActions().editingOccurrence.get()}
          save={() => updateOccurrence()}
        />
      ) : (
        false
      )}

      {occurrenceStatusActions().editingOccurrence.get() ? (
        <Grid item>
          <Tooltip title={translate('Cancel Edit')}>
            <IconButton
              onClick={() => {
                syncOccurrenceEditAttendent(occurrence().id.get()),
                  occurrenceStatusActions().editingOccurrence.set(false)
              }}
            >
              <EditOff />
            </IconButton>
          </Tooltip>
        </Grid>
      ) : (
        false
      )}
    </Grid>
  )
}
